import { type Attributes, type Context, TraceFlags, trace } from "@opentelemetry/api";
import { type Sampler, SamplingDecision, type SamplingResult } from "@opentelemetry/sdk-trace-web";

import { createLogger } from "@/logger";
import { TELEMETRY_CATEGORY_ATTRIBUTE } from "./categories";

const log = createLogger("telemetry");

/** Per-category sample rates in [0, 1]; unknown/missing categories fall back to `diagnostic`. */
export type TelemetrySampleRates = Record<string, number>;

/**
 * Default per-session sample rates, overridable via the LaunchDarkly JSON flag
 * `telemetrySampleRates`. The defaults are the always-on-for-every-customer budget; rough
 * per-session volume at rate 1.0, for reference:
 *
 * - `api` — one span per API request (bootstrap endpoint pages, AppSync batches, echo heartbeat).
 *   The highest steady-state volume: tens per boot plus ~1/minute while a tab is open.
 * - `perceived` — boot-to-interactive, feed opens, driver list load, fleet message UI/send,
 *   feed-content-painted. A handful to tens of spans/session; the core UX story, always on.
 * - `vitals` — LCP/INP/CLS/TTFB, a few final events per page lifecycle. Cheap, always on.
 * - `perf` — one aggregated PerfHub rollup per flush interval (~2/minute while active); the
 *   aggregate stands in for high-volume detail, so a 10% session sample gives fleet-wide trends.
 * - `reliability` — storage recovery, AppSync failures, error screens. Rare; never sampled away.
 * - `diagnostic` — high-detail drill-down spans (per-query timings, per-render profiling,
 *   per-page bootstrap db writes, warmup sub-steps) plus anything uncategorized. Ships nothing by
 *   default; flip on per user/workspace via LD (`{"diagnostic": 1}`) when investigating — the
 *   "aggressive metrics" mode.
 */
export const DEFAULT_SAMPLE_RATES: TelemetrySampleRates = {
  api: 0.2,
  perceived: 1,
  vitals: 1,
  perf: 0.1,
  reliability: 1,
  diagnostic: 0,
};

/**
 * Head sampler routing root spans by their `telemetry.category` attribute to a per-category rate.
 *
 * A single random draw is made per page load and compared to each category's rate, so a session's
 * spans are coherent end-to-end: a session sampled for a category keeps that category for its whole
 * life, and a session sampled at a low rate is also sampled at every higher rate. Child spans
 * follow their local parent's decision so traces stay intact.
 *
 * Sampled-in spans carry a `SampleRate` attribute (1/rate) so Honeycomb re-weights counts.
 */
export class SessionCategorySampler implements Sampler {
  private readonly sessionKey: number;
  private readonly warnedCategories = new Set<string>();

  public constructor(
    private readonly options: {
      /** Resolves the current LD-provided rates; merged over {@link DEFAULT_SAMPLE_RATES}. */
      getSampleRates?: () => TelemetrySampleRates | undefined;
      /** Kill switch: when true nothing is sampled, regardless of category. */
      isKilled?: () => boolean;
      /** Per-session random draw in [0, 1); injectable for tests. */
      sessionKey?: number;
    } = {},
  ) {
    this.sessionKey = options.sessionKey ?? Math.random();
  }

  public shouldSample(
    context: Context,
    _traceId: string,
    _spanName: string,
    _spanKind: unknown,
    attributes: Attributes,
  ): SamplingResult {
    if (this.options.isKilled?.()) {
      return { decision: SamplingDecision.NOT_RECORD };
    }

    // Children follow their local parent so traces stay intact. The parent's SampleRate weight is
    // not re-attached here (the original category isn't recoverable from context); child spans of
    // sampled traces are slightly under-weighted in Honeycomb, which is acceptable because nearly
    // every span in the browser is a root span.
    const parent = trace.getSpanContext(context);
    if (parent && trace.isSpanContextValid(parent) && !parent.isRemote) {
      return {
        decision:
          (parent.traceFlags & TraceFlags.SAMPLED) === TraceFlags.SAMPLED
            ? SamplingDecision.RECORD_AND_SAMPLED
            : SamplingDecision.NOT_RECORD,
      };
    }

    const rate = this.rateFor(attributes[TELEMETRY_CATEGORY_ATTRIBUTE]);
    if (rate <= 0 || this.sessionKey >= rate) {
      return { decision: SamplingDecision.NOT_RECORD };
    }
    return {
      decision: SamplingDecision.RECORD_AND_SAMPLED,
      // Honeycomb requires an integer SampleRate, so re-weighting is approximate for rates whose
      // reciprocal isn't whole (e.g. 0.3 weighs as 3 rather than 3.33).
      attributes: { SampleRate: Math.round(1 / rate) },
    };
  }

  public toString(): string {
    return "SessionCategorySampler";
  }

  private rateFor(category: unknown): number {
    const rates = { ...DEFAULT_SAMPLE_RATES, ...this.options.getSampleRates?.() };
    // A category string outside the taxonomy is almost certainly a typo, and it fails *silent*
    // (diagnostic ships nothing by default) — warn once per category so it's discoverable.
    if (typeof category === "string" && !(category in rates) && !this.warnedCategories.has(category)) {
      this.warnedCategories.add(category);
      log.warn("[sampler] Unknown telemetry category; treating as diagnostic (off by default)", { category });
    }
    const key = typeof category === "string" && category in rates ? category : "diagnostic";
    return Math.min(Math.max(rates[key] ?? 0, 0), 1);
  }
}
